import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Icon from '../../blocks.simple/icon/icon'
import styles from './ScrollableMenu-styles'
import { cn } from 'ethcss'

type ScrollDirection = 'next' | 'prev'
type MenuCategoryElement = React.ReactElement<{ className?: string; children?: React.ReactNode }>

interface IScrollableMenuProps {
    children: MenuCategoryElement | MenuCategoryElement[]
}

const getChildrenLength = (children: MenuCategoryElement[]) => {
    const firstCategory = children[0]
    return firstCategory ? React.Children.toArray(firstCategory.props.children).length : 0
}

export const ScrollableMenuView: React.FC<IScrollableMenuProps> = ({ children }) => {
    const categories = useMemo(
        () =>
            React.Children.toArray(children).filter((child): child is MenuCategoryElement =>
                React.isValidElement(child)
            ),
        [children]
    )
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isAvailableArrows, setIsAvailableArrows] = useState(true)
    const [isRightLimit, setIsRightLimit] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const scrollAreaRef = useRef<HTMLDivElement>(null)
    const shiftLeftRef = useRef(30)
    const childrenLength = getChildrenLength(categories)

    const getMenuElement = useCallback((): HTMLElement | null => {
        const className = categories[0]?.props.className

        if (!className) return null

        const element = document.getElementsByClassName(className).item(0)
        return element instanceof HTMLElement ? element : null
    }, [categories])

    const shiftOverflowItem = (item: HTMLElement, items: HTMLCollection) => {
        const rightBorder = item.offsetLeft + (scrollAreaRef.current?.offsetWidth ?? 0)

        for (let i = 0; i < items.length; i++) {
            const menuItem = items.item(i)

            if (!(menuItem instanceof HTMLElement)) continue

            const itemLeftBorder = menuItem.offsetLeft

            if (rightBorder > itemLeftBorder) {
                const subMenuWrapperNode = menuItem.children.item(1)

                if (subMenuWrapperNode instanceof HTMLElement && subMenuWrapperNode.children.length) {
                    const subMenu = subMenuWrapperNode.children.item(0)

                    if (!(subMenu instanceof HTMLElement)) continue

                    const subMenuWidth = subMenu.offsetWidth
                    const subMenuRightBorder = itemLeftBorder + subMenuWidth

                    if (subMenuRightBorder > rightBorder) {
                        const subItemOverflowValue = rightBorder - subMenuRightBorder
                        subMenu.style.left = subItemOverflowValue + 'px'
                    } else {
                        subMenu.style.left = '0px'
                    }
                }
            }
        }
    }

    const slideMenu = useCallback(
        (direction: ScrollDirection) => {
            const menu = getMenuElement()

            if (!menu) return

            let itemsWidth = 0
            const isNext = direction === 'next'
            const items = menu.children
            const defaultStep = 2
            const actionStep = isNext
                ? currentIndex + defaultStep < items.length - 1
                    ? defaultStep
                    : items.length - 1 - currentIndex
                : currentIndex - defaultStep > 0
                ? defaultStep
                : // : 0
                  1
            const itemIndex = isNext
                ? currentIndex + actionStep
                : currentIndex - actionStep === currentIndex
                ? 0
                : currentIndex - actionStep
            const item = items.item(itemIndex)

            if (!(item instanceof HTMLElement)) return

            const moveValue = -item.offsetLeft

            Array.from(menu.children).forEach((child) => {
                if (child instanceof HTMLElement) itemsWidth += child.offsetWidth
            })

            menu.style.transform = `translateX(${moveValue + shiftLeftRef.current}px)`

            setCurrentIndex(
                isNext
                    ? currentIndex + actionStep
                    : currentIndex - actionStep === currentIndex
                    ? 0
                    : currentIndex - actionStep
            )
            shiftLeftRef.current = 0

            shiftOverflowItem(item, items)

            setIsRightLimit(itemsWidth - (item.offsetLeft + shiftLeftRef.current) - menu.offsetWidth < 0)
        },
        [currentIndex, getMenuElement]
    )

    const checkAvailableArrows = useCallback(() => {
        const menu = getMenuElement()

        if (menu) {
            const itemsSum = Array.from(menu.children).reduce((sum, item) => {
                return item instanceof HTMLElement ? sum + item.offsetWidth : sum
            }, 0)

            setIsAvailableArrows(itemsSum + 60 > (menuRef.current?.offsetWidth ?? 0))
            slideMenu('prev')
        }
    }, [getMenuElement, slideMenu])

    useEffect(() => {
        checkAvailableArrows()

        window.addEventListener('resize', checkAvailableArrows)

        return () => window.removeEventListener('resize', checkAvailableArrows)
    }, [categories.length, checkAvailableArrows])

    return (
        <div ref={menuRef} className={cn(categories.length ? styles.borderStyles : '', styles.scrollableMenu)}>
            {isAvailableArrows ? (
                <Icon
                    containerClass={cn(styles.arrow, styles.arrowLeft)}
                    onClick={() => slideMenu('prev')}
                    type={'left_icon_menu'}
                    size={14}
                    disabled={currentIndex === 0}
                />
            ) : null}
            <div ref={scrollAreaRef} className={styles.scrollArea}>
                {children}
            </div>
            {isAvailableArrows ? (
                <Icon
                    containerClass={cn(styles.arrow, styles.arrowRight)}
                    onClick={() => slideMenu('next')}
                    type={'right_icon_menu'}
                    size={14}
                    disabled={currentIndex >= childrenLength - 1 || isRightLimit}
                />
            ) : null}
        </div>
    )
}
